import Button from "@/common/elements/Button";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledFlex, StyledGridCol, StyledGridRow } from "@/common/styles";
import { getOptionsFromRefList, hasCheckSecurity, isNonEmptyVariable } from "@/util/common_function";
import { Form } from "antd";
import CommonDatePicker from "@/common/elements/DatePicker";
import dayjs from "dayjs";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import { MODULES_LIST_WITH_ID, SUB_MODULES_LIST_WITH_ID } from "@/util/common_variable";
import { useSelector } from "react-redux";

// Converts strings like "True" and booleans to a reliable boolean for checkbox.
const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    return ["true", "1", "y", "yes"].includes(value.trim().toLowerCase());
  }
  return false;
};

export const AliasAddForm = forwardRef(
  ({ formData, isQuickAdd = false, quickSave, bookingsFieldsEnabled }, ref) => {
    const [form] = Form.useForm();
    const [bioAlias, setBioAlias] = useState({ dob: null });
    const [saveBtnVisible, setSaveBtnVisible] = useState(false);
    const fullSearch = useSelector(state => state.fullSearch);

    useEffect(() => {
      setBioAlias(() => ({
        ...formData,
        dob: formData?.dob ? dayjs(formData?.dob) : null,
        questionedIdentity: toBoolean(formData?.questionedIdentity),
      }));
      if (
        isNonEmptyVariable(formData?.lName) ||
        isNonEmptyVariable(formData?.fName) ||
        isNonEmptyVariable(formData?.mName)
      ) {
        setSaveBtnVisible(true);
      } else {
        setSaveBtnVisible(false);
      }
    }, [formData]);

    useEffect(() => {
      form.setFieldsValue(bioAlias);
    }, [bioAlias, form]);

    useImperativeHandle(ref, () => ({
      submitForm: () => form.submit(),
      resetForm: () => form.resetFields(),
      getFormValues: () => form.getFieldsValue(),
      getFieldsError: () => form.getFieldsError(),
      isFieldsValidating: () => form.isFieldsValidating(),
      validateFields: () => form.validateFields(),
    }));

    return (
      <Form
        form={form}
        disabled={!bookingsFieldsEnabled}
        initialValues={bioAlias}
        onFieldsChange={() => {
          const data = form.getFieldsValue();
          setSaveBtnVisible(
            isNonEmptyVariable(data?.lName) ||
              isNonEmptyVariable(data?.fName) ||
              isNonEmptyVariable(data?.mName)
          );
        }}
      >
        <StyledGridRow gutter={16}>
          <StyledGridCol>
            <FormItem
              name="lName"
              label="Last Name"
              required={!fullSearch}
              rules={
                !fullSearch
                  ? [
                      {
                        required: true,
                        message: "Last Name is required!",
                      },
                    ]
                  : []
              }
            >
              <TextField />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol>
            <FormItem name="fName" label="First Name">
              <TextField />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol>
            <FormItem name="mName" label="Middle Name">
              <TextField />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={4}>
            <FormItem name="suffix" label="Suffix">
              <Select
                options={getOptionsFromRefList("Suffix")}
                placeholder={"Suffix"}
                showSearch={false}
                variant="outlined"
                disabled={!bookingsFieldsEnabled}
              />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol>
            <FormItem name="dob" label="DOB">
              <CommonDatePicker
                placeholder="MM/DD/YYYY"
                format="MM/DD/YYYY"
                showTime={false}
                disableFutureDates={true}
                inputProps={{
                  maxLength: 10,
                }}
                disabled={!bookingsFieldsEnabled}
              />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol lg={16}>
            <StyledFlex align="start" gap="14px">
              <FormItem
                name="ssn"
                label="SSN"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const ssnPattern = /^(?:\d{3}-\d{2}-\d{4}|\d{9}|UNKNOWN)$/;
                      if (!ssnPattern.test(value)) {
                        return Promise.reject(
                          "The value for SSN is not in the correct format. It should be a valid Social Security Number or 'UNKNOWN'. You can enter the Social Security Number with or without the dashes. For example, you could enter either '123456789' or '123-45-6789'."
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <TextField />
              </FormItem>
              <StyledFlex>
                <FormItem
                  name="questionedIdentity"
                  valuePropName="checked"
                  label={
                    <span onClick={(e) => e.preventDefault()}>
                      Questioned Identity
                    </span>
                  }
                  layout="horizontal"
                  labelPostion="label-right"
                  paddingTop="24px"
                  style={{ minWidth: "200px" }}
                >
                  <Checkbox />
                </FormItem>
                <FormItem label="" paddingTop="24px">
                  {isQuickAdd && (
                    <Button
                      disabled={
                        !saveBtnVisible ||
                        !bookingsFieldsEnabled ||
                        !hasCheckSecurity(
                          Security_Types.functionality,
                          Entity_types.subModule,
                          LABELS_CAPITAL.ADD,
                          MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                          SUB_MODULES_LIST_WITH_ID.Arrest_Report
                        )
                      }
                      onClick={quickSave}
                    >
                      Q-Add
                    </Button>
                  )}
                </FormItem>
              </StyledFlex>
            </StyledFlex>
          </StyledGridCol>
        </StyledGridRow>
      </Form>
    );
  }
);

export default AliasAddForm;
